import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext, usePerformersApi } from "@/context";
import { Breadcrumbs } from "@/utils/components/Breadcrumbs";

interface PerformerFormData {
  performerName: string;
  bio: string;
  primaryCategory: string;
  secondaryCategory: string;
  tags: string[];
  website: string;
  hometown: {
    city?: string;
    state?: string;
    country?: string;
  };
  socialMedia: {
    facebook?: string;
    instagram?: string;
    spotify?: string;
    youtube?: string;
  };
  technicalRider: string;
  hospitalityRider: string;
  isActive: boolean;
}

const CATEGORY_CHOICES = [
  "Band",
  "Solo Artist",
  "DJ",
  "Ensemble",
  "Orchestra",
  "Choir",
  "Comedy",
  "Theater",
  "Dance",
  "Other",
];

// Dynamic secondary options based on primary category
const SECONDARY_OPTIONS: Record<string, string[]> = {
  Band: [
    "Rock",
    "Pop",
    "Jazz",
    "Blues",
    "Country",
    "Metal",
    "Punk",
    "Indie",
    "Alternative",
    "Folk",
    "Reggae",
  ],
  "Solo Artist": [
    "Rock",
    "Pop",
    "Jazz",
    "Blues",
    "Country",
    "R&B",
    "Soul",
    "Folk",
    "Singer-Songwriter",
    "Electronic",
  ],
  DJ: [
    "House",
    "Techno",
    "Trance",
    "Dubstep",
    "Drum & Bass",
    "Hip Hop",
    "Electronic",
    "EDM",
    "Mashup",
  ],
  Ensemble: [
    "Classical",
    "Jazz",
    "Chamber Music",
    "String Quartet",
    "Brass",
    "Contemporary",
  ],
  Orchestra: [
    "Classical",
    "Symphony",
    "Chamber",
    "Philharmonic",
    "Baroque",
    "Contemporary",
  ],
  Choir: [
    "Gospel",
    "Classical",
    "Contemporary",
    "A Cappella",
    "Sacred",
    "Secular",
  ],
  Comedy: [
    "Stand-up",
    "Improv",
    "Sketch",
    "Musical Comedy",
    "Dark Comedy",
    "Observational",
    "Political",
    "Physical",
  ],
  Theater: [
    "Drama",
    "Musical",
    "Comedy",
    "Tragedy",
    "Experimental",
    "One-Person Show",
    "Cabaret",
  ],
  Dance: [
    "Ballet",
    "Contemporary",
    "Hip Hop",
    "Jazz",
    "Tap",
    "Modern",
    "Ballroom",
    "Cultural",
  ],
  Other: [
    "Variety",
    "Spoken Word",
    "Magic",
    "Circus",
    "Performance Art",
    "Multi-disciplinary",
  ],
};

const initialFormData: PerformerFormData = {
  performerName: "",
  bio: "",
  primaryCategory: "",
  secondaryCategory: "",
  tags: [],
  website: "",
  hometown: {},
  socialMedia: {},
  technicalRider: "",
  hospitalityRider: "",
  isActive: true,
};

export const PerformerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // team not required for performer create/update via this form
  useAppContext();
  const performersApi = usePerformersApi();
  const [formData, setFormData] = useState<PerformerFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [duplicateCheck, setDuplicateCheck] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");

  const isEditMode = Boolean(id);

  useEffect(() => {
    if (isEditMode) {
      fetchPerformer();
    }
  }, [id]);

  const fetchPerformer = async () => {
    try {
      setLoading(true);
      const data = await performersApi.performersRetrieve({ id: id! });

      setFormData({
        performerName: data.performerName || "",
        bio: data.bio || "",
        primaryCategory: data.primaryCategory || "",
        secondaryCategory: "", // Not in API response
        tags: data.tags || [],
        website: data.website || "",
        hometown: data.hometown || {},
        socialMedia: data.socialMedia || {},
        technicalRider: data.technicalRider || "",
        hospitalityRider: data.hospitalityRider || "",
        isActive: data.isActive !== false,
      });
    } catch {
      setError("Failed to load performer. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const checkDuplicate = async (name: string) => {
    if (!name.trim() || name === formData.performerName) return;

    try {
      // TODO: API check_duplicate endpoint requires ID, not name query param
      // For now, skip client-side duplicate check - backend will validate
      setDuplicateCheck(null);
    } catch {
      // Ignore duplicate check errors
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (duplicateCheck && !isEditMode) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      // Build payload using camelCase property names expected by the generated TypeScript client
      // The OpenAPI-generated serializers map these to snake_case when sending to the server.
      const payload = {
        performerName: formData.performerName,
        bio: formData.bio,
        primaryCategory: formData.primaryCategory,
        secondaryCategory: formData.secondaryCategory,
        tags: formData.tags,
        website: formData.website,
        hometown: formData.hometown,
        socialMedia: formData.socialMedia,
        technicalRider: formData.technicalRider,
        hospitalityRider: formData.hospitalityRider,
        isActive: formData.isActive,
      };

      if (isEditMode) {
        // cast API client to any to avoid generator type mismatches
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (performersApi as any).performersPartialUpdate({
          id: id!,
          patchedPerformer: payload,
        });
      } else {
        // cast API client to any to avoid generator type mismatches
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (performersApi as any).performersCreate({
          performer: payload,
        });
      }

      navigate("/performers");
    } catch (err: unknown) {
      // Parse Django REST Framework validation errors
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (err && typeof err === "object" && "body" in err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiError = err as any;
        if (apiError.body && typeof apiError.body === "object") {
          const errors = apiError.body;
          const newFieldErrors: Record<string, string> = {};

          // Map snake_case field names to camelCase for display
          const fieldMapping: Record<string, string> = {
            performer_name: "performerName",
            primary_category: "primaryCategory",
            secondary_category: "secondaryCategory",
            social_media: "socialMedia",
            technical_rider: "technicalRider",
            hospitality_rider: "hospitalityRider",
            is_active: "isActive",
          };

          for (const [key, value] of Object.entries(errors)) {
            const displayKey = fieldMapping[key] || key;
            if (Array.isArray(value)) {
              newFieldErrors[displayKey] = value.join(", ");
            } else if (typeof value === "string") {
              newFieldErrors[displayKey] = value;
            }
          }

          if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            setError("Please correct the errors below.");
          } else {
            setError("Failed to save performer. Please try again.");
          }
        } else {
          setError(
            String(
              apiError.message || "Failed to save performer. Please try again.",
            ),
          );
        }
      } else {
        setError(
          (err as Error).message ||
            "Failed to save performer. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  if (loading && isEditMode) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading performer...</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Breadcrumbs
        items={
          isEditMode
            ? [
                { label: "Performers", path: "/performers" },
                {
                  label: formData.performerName || "Loading...",
                  path: `/performers/${id}`,
                },
                { label: "Edit" },
              ]
            : [
                { label: "Performers", path: "/performers" },
                { label: "New Performer" },
              ]
        }
      />
      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Performer" : "New Performer"}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              {/* Basic Information */}
              <Typography variant="h6">Basic Information</Typography>

              <TextField
                label="Performer Name"
                required
                fullWidth
                value={formData.performerName}
                onChange={(e) => {
                  setFormData({ ...formData, performerName: e.target.value });
                  setDuplicateCheck(null);
                  setFieldErrors({ ...fieldErrors, performerName: "" });
                }}
                onBlur={(e) => checkDuplicate(e.target.value)}
                error={Boolean(duplicateCheck || fieldErrors.performerName)}
                helperText={duplicateCheck || fieldErrors.performerName}
              />

              <TextField
                label="Biography"
                multiline
                rows={4}
                fullWidth
                value={formData.bio}
                onChange={(e) =>
                  setFormData({ ...formData, bio: e.target.value })
                }
                helperText="Tell us about this performer"
              />

              {/* Hometown */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hometown
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="City"
                    fullWidth
                    value={formData.hometown.city || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hometown: {
                          ...formData.hometown,
                          city: e.target.value,
                        },
                      })
                    }
                    placeholder="e.g., Nashville"
                  />
                  <TextField
                    label="State/Region"
                    fullWidth
                    value={formData.hometown.state || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hometown: {
                          ...formData.hometown,
                          state: e.target.value,
                        },
                      })
                    }
                    placeholder="e.g., Tennessee"
                  />
                  <TextField
                    label="Country"
                    fullWidth
                    value={formData.hometown.country || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hometown: {
                          ...formData.hometown,
                          country: e.target.value,
                        },
                      })
                    }
                    placeholder="e.g., USA"
                  />
                </Stack>
              </Box>

              {/* Entertainment Type */}
              <Typography variant="h6">Entertainment Type</Typography>

              <TextField
                label="Primary Category"
                select
                required
                fullWidth
                value={formData.primaryCategory}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    primaryCategory: e.target.value,
                    secondaryCategory: "", // Reset secondary when primary changes
                  });
                  setFieldErrors({ ...fieldErrors, primaryCategory: "" });
                }}
                error={Boolean(fieldErrors.primaryCategory)}
                helperText={fieldErrors.primaryCategory}
              >
                {CATEGORY_CHOICES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>

              {/* Secondary Category as Tag Cloud */}
              {formData.primaryCategory &&
                SECONDARY_OPTIONS[formData.primaryCategory] && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Genre/Style (optional)
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {SECONDARY_OPTIONS[formData.primaryCategory].map(
                        (option) => (
                          <Chip
                            key={option}
                            label={option}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                secondaryCategory: option,
                              })
                            }
                            color={
                              formData.secondaryCategory === option
                                ? "primary"
                                : "default"
                            }
                            variant={
                              formData.secondaryCategory === option
                                ? "filled"
                                : "outlined"
                            }
                          />
                        ),
                      )}
                      {formData.secondaryCategory && (
                        <Chip
                          label="Clear"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              secondaryCategory: "",
                            })
                          }
                          color="error"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Stack>
                  </Box>
                )}

              {/* Tags */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  mb={2}
                  flexWrap="wrap"
                >
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                    />
                  ))}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label="New tag"
                    placeholder="Add tag..."
                    sx={{ flex: 1, minWidth: 0 }}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), handleAddTag())
                    }
                  />
                  <Button
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                    startIcon={<AddIcon />}
                    onClick={handleAddTag}
                  >
                    Add
                  </Button>
                </Stack>
              </Box>

              <Divider />

              {/* Online Presence */}
              <Typography variant="h6">Online Presence</Typography>

              <TextField
                label="Website"
                fullWidth
                type="url"
                value={formData.website}
                onChange={(e) =>
                  setFormData({ ...formData, website: e.target.value })
                }
                placeholder="https://..."
              />

              <Stack spacing={2}>
                <TextField
                  label="Facebook URL"
                  fullWidth
                  value={formData.socialMedia.facebook || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: {
                        ...formData.socialMedia,
                        facebook: e.target.value,
                      },
                    })
                  }
                  placeholder="https://facebook.com/..."
                />
                <TextField
                  label="Instagram URL"
                  fullWidth
                  value={formData.socialMedia.instagram || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: {
                        ...formData.socialMedia,
                        instagram: e.target.value,
                      },
                    })
                  }
                  placeholder="https://instagram.com/..."
                />
                <TextField
                  label="Spotify URL"
                  fullWidth
                  value={formData.socialMedia.spotify || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: {
                        ...formData.socialMedia,
                        spotify: e.target.value,
                      },
                    })
                  }
                  placeholder="https://open.spotify.com/artist/..."
                />
                <TextField
                  label="YouTube URL"
                  fullWidth
                  value={formData.socialMedia.youtube || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: {
                        ...formData.socialMedia,
                        youtube: e.target.value,
                      },
                    })
                  }
                  placeholder="https://youtube.com/..."
                />
              </Stack>

              <Divider />

              {/* Technical Requirements */}
              <Typography variant="h6">Technical Requirements</Typography>

              <TextField
                label="Technical Rider"
                multiline
                rows={4}
                fullWidth
                value={formData.technicalRider}
                onChange={(e) =>
                  setFormData({ ...formData, technicalRider: e.target.value })
                }
                helperText="Stage plot, equipment needs, sound requirements, etc."
              />

              <TextField
                label="Hospitality Rider"
                multiline
                rows={4}
                fullWidth
                value={formData.hospitalityRider}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hospitalityRider: e.target.value,
                  })
                }
                helperText="Green room requirements, catering, accommodations, etc."
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                }
                label="Active (visible to team members)"
              />

              {/* Actions */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate("/performers")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || Boolean(duplicateCheck)}
                >
                  {loading ? "Saving..." : isEditMode ? "Update" : "Create"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
